import { useEffect, useState } from 'react'
import api from '../services/api'

// Carteira de Plano de Saúde (TMOTCPL: frota.motcpl + carências frota.motcpc). Linhas de carência numeradas sozinhas.

interface Carencia { lin: number; cdc: string; cds: string }
interface Plano { plscod: string; plstip: string; plsnum: string }
interface Form { pla: string; num: string; dti: string; dtf: string }

const largura = (max: number) => Math.max(56, max * 9 + 28)
const LINHA: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: '0.75rem 0.9rem', alignItems: 'flex-end' }
const th: React.CSSProperties = { textAlign: 'left', padding: '0.4rem 0.6rem', fontSize: '0.72rem' }
const td: React.CSSProperties = { padding: '0.3rem 0.5rem', fontSize: '0.82rem' }

const campo = (w: number, label: string, conteudo: React.ReactNode) => (
  <div className="field-cad" style={{ flex: `0 0 ${w}px` }}>
    <label style={{ whiteSpace: 'nowrap' }}>{label}</label>
    {conteudo}
  </div>
)

export default function FuncionarioPlanoSaudeModal({ motcod, motnome, onClose }: { motcod: string; motnome: string; onClose: () => void }) {
  const [f, setF] = useState<Form | null>(null)
  const [car, setCar] = useState<Carencia[]>([])
  const [planos, setPlanos] = useState<Plano[]>([])
  const [erro, setErro] = useState('')
  const [aviso, setAviso] = useState('')
  const [salvando, setSalvando] = useState(false)

  async function carregar() {
    const r = await api.get(`/funcionarios/${encodeURIComponent(motcod)}/plano-saude`)
    setF({ pla: r.data.pla, num: r.data.num, dti: r.data.dti, dtf: r.data.dtf })
    setCar(r.data.carencias)
    setPlanos(r.data.planos)
  }

  useEffect(() => {
    carregar().catch(() => setErro('Não foi possível carregar o plano de saúde.'))
  }, [motcod])

  async function salvar() {
    setErro(''); setAviso('')
    setSalvando(true)
    try {
      const r = await api.put(`/funcionarios/${encodeURIComponent(motcod)}/plano-saude`, { ...f, carencias: car })
      await carregar()
      setAviso(r.data?.semAlteracao ? 'Nenhuma alteração para salvar.' : 'Plano de saúde salvo.')
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao salvar o plano de saúde.')
    } finally {
      setSalvando(false)
    }
  }

  const set = (c: keyof Form, v: string) => setF(p => ({ ...p!, [c]: v }))
  const setLinha = (i: number, c: 'cdc' | 'cds', v: string) => setCar(cs => cs.map((x, j) => (j === i ? { ...x, [c]: v } : x)))
  // plano gravado que não existe mais no cadastro continua aparecendo
  const opcoes = f && f.pla && !planos.some(p => p.plscod === f.pla) ? [{ plscod: f.pla, plstip: '(não cadastrado)', plsnum: '' }, ...planos] : planos

  return (
    <div className="modal-overlay" style={{ zIndex: 1300 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: 700, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header" style={{ gap: '1rem' }}>
          <div style={{ minWidth: 0 }}><h2>{`Carteira de Plano de Saúde — ${motcod} · ${motnome}`}</h2></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0, marginLeft: 'auto' }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'rgba(219,234,254,0.8)' }}>TMOTCPL</span>
            <button className="modal-fechar" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="modal-corpo">
          {!f ? (
            <p style={{ color: erro ? '#dc2626' : '#6b7280' }}>{erro || 'Carregando...'}</p>
          ) : (
            <>
              <div style={LINHA}>
                {campo(260, 'Código Plano',
                  <select value={f.pla} style={{ width: '100%', boxSizing: 'border-box' }} onChange={e => set('pla', e.target.value)}>
                    <option value="">(sem plano)</option>
                    {opcoes.map(p => <option key={p.plscod} value={p.plscod}>{p.plscod} — {p.plstip}</option>)}
                  </select>)}
                {campo(largura(25), 'Número',
                  <input value={f.num} maxLength={25} style={{ width: '100%', boxSizing: 'border-box' }} onChange={e => set('num', e.target.value)} />)}
              </div>
              <div style={{ ...LINHA, marginTop: '0.75rem' }}>
                {campo(140, 'Inclusão',
                  <input type="date" value={f.dti} style={{ width: '100%', boxSizing: 'border-box' }} onChange={e => set('dti', e.target.value)} />)}
                {campo(140, 'Validade',
                  <input type="date" value={f.dtf} min={f.dti || undefined} style={{ width: '100%', boxSizing: 'border-box' }} onChange={e => set('dtf', e.target.value)} />)}
              </div>

              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1a6ea8', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '1.1rem 0 0.5rem' }}>
                Carências
              </div>
              <table className="table" style={{ width: '100%' }}>
                <thead>
                  <tr><th style={{ ...th, width: 50 }}>Lin</th><th style={{ ...th, width: 150 }}>Data Carência</th><th style={th}>Descrição</th><th style={{ ...th, width: 70 }}></th></tr>
                </thead>
                <tbody>
                  {car.length === 0 && (
                    <tr><td colSpan={4} style={{ ...td, textAlign: 'center', color: '#6b7280', padding: '1rem' }}>Nenhuma carência lançada.</td></tr>
                  )}
                  {car.map((c, i) => (
                    <tr key={c.lin || `nova-${i}`}>
                      <td style={{ ...td, color: '#6b7280' }}>{c.lin || '—'}</td>
                      <td style={td}>
                        <input type="date" value={c.cdc} style={{ width: '100%', boxSizing: 'border-box', padding: '0.35rem 0.5rem', fontSize: '0.82rem' }}
                          onChange={e => setLinha(i, 'cdc', e.target.value)} />
                      </td>
                      <td style={td}>
                        <input value={c.cds} maxLength={40} style={{ width: largura(40), maxWidth: '100%', boxSizing: 'border-box', padding: '0.35rem 0.5rem', fontSize: '0.82rem' }}
                          onChange={e => setLinha(i, 'cds', e.target.value.toUpperCase())} />
                      </td>
                      <td style={td}>
                        <button className="btn btn-outline btn-sm" style={{ color: '#b91c1c' }} onClick={() => setCar(cs => cs.filter((_, j) => j !== i))}>Excluir</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button className="btn btn-secondary btn-sm" style={{ marginTop: '0.75rem' }} onClick={() => setCar(cs => [...cs, { lin: 0, cdc: '', cds: '' }])}>
                + Nova linha
              </button>
            </>
          )}

          {f && erro && <p style={{ color: '#dc2626', fontSize: '0.82rem', margin: '1rem 0 0' }}>{erro}</p>}
          {aviso && <p style={{ color: '#16a34a', fontSize: '0.82rem', margin: '1rem 0 0' }}>{aviso}</p>}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Fechar</button>
          <button className="btn btn-orange" onClick={salvar} disabled={salvando || !f}>{salvando ? 'Salvando...' : 'Salvar'}</button>
        </div>
      </div>
    </div>
  )
}
